import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { MapPin, GraduationCap, Heart, Calendar, Loader2, Trash2, X, UserPlus, RotateCcw, Mail, Link2, Copy, Check } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useSignedPhotoUrl } from "@/lib/photoUrl";

interface Profile {
  id: string;
  first_name: string;
  age: number;
  gender: string;
  location: string;
  education_level: string;
  religiosity_level: string;
  prayer_frequency: string;
  bio?: string;
  profile_picture_url?: string;
  is_active: boolean;
  profile_completed?: boolean;
  created_at: string;
  status: string | null;
  invite_email: string | null;
  claim_token: string | null;
  claimed_at: string | null;
}

interface ProfileCardProps {
  profile: Profile;
  onViewDetails: (profile: Profile) => void;
  onCreateMatch?: (profileId: string) => void;
  onDelete?: (profileId: string) => void;
  onDismiss?: (profileId: string) => void;
  onUndismiss?: (profileId: string) => void;
  matchStatus?: "pending" | "accepted" | "rejected" | null;
  isLoading?: boolean;
  isDismissed?: boolean;
  showFindMatches?: boolean;
  isAdmin?: boolean;
}

export function ProfileCard({ profile, onViewDetails, onCreateMatch, onDelete, onDismiss, onUndismiss, matchStatus, isLoading, isDismissed, showFindMatches = false, isAdmin = false }: ProfileCardProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const signedProfilePicture = useSignedPhotoUrl(profile.profile_picture_url);

  const truncatedBio = profile.bio
    ? (profile.bio.length > 100 ? profile.bio.substring(0, 100) + "..." : profile.bio)
    : "No bio provided";

  const claimUrl = profile.claim_token
    ? `${window.location.origin}/claim-profile/${profile.claim_token}`
    : null;

  const copyClaimLink = async () => {
    if (claimUrl) {
      await navigator.clipboard.writeText(claimUrl);
      setCopied(true);
      toast.success("Claim link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card className="hover:border-primary/50 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage
              src={signedProfilePicture ?? undefined}
              alt={profile.first_name}
              className={isAdmin ? "blur-md" : ""}
            />
            <AvatarFallback className="text-lg">
              {profile.first_name[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-lg">
                    {profile.first_name}, {profile.age}
                  </h3>
                  {profile.is_active ? (
                    <Badge variant="default" className="text-xs">Active</Badge>
                  ) : profile.profile_completed === false ? (
                    <Badge variant="outline" className="text-xs">Profile incomplete</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Paused</Badge>
                  )}
                  {profile.status && profile.status !== "active" && (
                    <Badge
                      variant={
                        profile.status === "pending" ? "outline" :
                        profile.status === "declined" ? "destructive" :
                        "secondary"
                      }
                      className="text-xs"
                    >
                      {profile.status === "pending" ? "Awaiting Claim" :
                       profile.status === "declined" ? "Declined" :
                       profile.status}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground capitalize">
                  {profile.gender === "male" ? "Brother" : "Sister"}
                </p>
              </div>
            </div>

            <div className="grid gap-2 text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{profile.location}</span>
              </div>

              <div className="flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-muted-foreground" />
                <span>{profile.education_level}</span>
              </div>

              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-muted-foreground" />
                <span className="capitalize">{profile.religiosity_level}</span>
                <span className="text-muted-foreground">• {profile.prayer_frequency}</span>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>Joined {formatDistanceToNow(new Date(profile.created_at), { addSuffix: true })}</span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground line-clamp-2">
              {truncatedBio}
            </p>

            {/* Pending Profile Info */}
            {profile.status === "pending" && profile.invite_email && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-900 dark:text-amber-100 font-medium">
                    Invited: {profile.invite_email}
                  </span>
                </div>
                {claimUrl && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyClaimLink}
                      className="h-7 text-xs text-amber-700 hover:text-amber-900 dark:text-amber-300"
                    >
                      {copied ? (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3 mr-1" />
                          Copy Claim Link
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => onViewDetails(profile)}
              >
                View Full Profile
              </Button>

              {showFindMatches && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const matcherPath = isAdmin ? '/admin/smart-matcher' : '/matchmaker/smart-matcher';
                    navigate(`${matcherPath}?profileId=${profile.id}`);
                  }}
                  title="Find matches for this profile"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              )}

              {onCreateMatch && (
                matchStatus ? (
                  <Button
                    variant={matchStatus === "accepted" ? "default" : matchStatus === "rejected" ? "destructive" : "secondary"}
                    size="sm"
                    className="flex-1"
                    disabled
                  >
                    {matchStatus === "pending" && "Match Pending"}
                    {matchStatus === "accepted" && "Match Accepted ✓"}
                    {matchStatus === "rejected" && "Match Rejected"}
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={() => onCreateMatch(profile.id)}
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating..." : "Create Match"}
                  </Button>
                )
              )}

              {onDismiss && !isDismissed && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDismiss(profile.id)}
                  title="Dismiss this match"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}

              {onUndismiss && isDismissed && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUndismiss(profile.id)}
                  title="Restore this match"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}

              {onDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(profile.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
